<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import MatchPreparation from '../components/match/MatchPreparation.svelte';
  import { settingsStore, refreshSettings } from '../stores/settings';
  import type { Match } from '../types';
  import { trackEvent } from '../lib/apiService';
  import { saveReportWithImage } from '../lib/utils/reportUtils';
  import { showToast } from '../lib/toast';
  import { _ } from 'svelte-i18n';

  let loading = true;
  let matchPreparationRef;

  // Default meeting time
  const DEFAULT_MEETING_TIME = "09:30";

  // Get the next Saturday
  function getNextSaturday() {
    const today = new Date();
    const currentDay = today.getDay(); // 0 = Sunday, 6 = Saturday
    const daysUntilSaturday = (6 - currentDay + 7) % 7; // Calculate days until next Saturday

    // If today is Saturday and it's early in the day, use today
    if (currentDay === 6 && today.getHours() < 12) {
      return today;
    }

    // Otherwise, get next Saturday
    const nextSaturday = new Date(today);
    nextSaturday.setDate(today.getDate() + daysUntilSaturday);
    nextSaturday.setHours(10, 0, 0); // Set to 10:00:00
    return nextSaturday;
  }

  function formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:00`;
  }

  // Create empty match object with default values
  const defaultMatchDay = getNextSaturday();
  let match: Match = {
    id: null,
    team_id: null, // Will be set once settings load
    match_date: formatDateForInput(defaultMatchDay),
    meeting_time: DEFAULT_MEETING_TIME,
    opponent_club: null,
    opponent_team_name: '',
    location: 'home',
    score_team: 0,
    score_opponent: 0,
    announcement_photo: null,
    announcement_photo_filename: null,
    match_photo: null,
    match_photo_filename: null,
    match_report: '',
    task_list: [],
    meeting_location: null, // Will be set once settings load
  };

  function handleClose() {
    // Navigate back to home when cancelled
    push('/');
  }

  async function handleMatchSave(event) {
    console.log('handleMatchSave called', event.detail);
    const { match: matchData } = event.detail;

    // Validate that club is selected
    if (!matchData.opponent_club || !matchData.opponent_club.id) {
      console.log('Validation failed: no club selected');
      showToast($_('new_match.errors.select_club'), 'error');
      return;
    }

    // Validate that opponent team name is provided
    if (!matchData.opponent_team_name || matchData.opponent_team_name.trim() === '') {
      console.log('Validation failed: no team name');
      showToast($_('new_match.errors.enter_team_name'), 'error');
      return;
    }

    console.log('Validation passed, saving match...', matchData);

    try {
      // Save the match to the backend
      const savedMatch = await saveReportWithImage(matchData);
      console.log('Match saved successfully:', savedMatch);

      // Track the event
      trackEvent('create_match', {
        module: "matches",
        action_type: "create_data",
        description: 'Create a match',
      }, $settingsStore.user.id);

      // Navigate to the newly created match
      console.log('Navigating to match details:', savedMatch.id);
      push(`/match/${savedMatch.id}/details`);
    } catch (error) {
      console.error('Error creating match:', error);
      // Error is already handled by saveReportWithImage with toast
    }
  }

  onMount(async () => {
    // Ensure settings are loaded before rendering
    if (!$settingsStore.active_team.id) {
      await refreshSettings();
    }

    // Set team_id and meeting_location from settings
    match.team_id = $settingsStore.active_team.id;
    match.meeting_location = $settingsStore.active_team.default_meeting_location;

    loading = false;

    // Wait for component to render, then auto-open the dialog
    await new Promise(resolve => setTimeout(resolve, 100));
    if (matchPreparationRef && matchPreparationRef.openEditDialog) {
      matchPreparationRef.openEditDialog();
    }
  });
</script>

{#if loading}
  <div class="loading-container">
    <p>Loading...</p>
  </div>
{:else}
  <MatchPreparation
    bind:this={matchPreparationRef}
    bind:match={match}
    sessionInfo={$settingsStore}
    isNewMatch={true}
    on:save={handleMatchSave}
    on:cancel={handleClose}
  />
{/if}

<style>
  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
  }
</style>
